import { buildLocalCoverUrl, buildLocalThumbUrl } from '../lib/connector';

/**
 * Checks whether a cover art URL is known-bad and should not be rendered.
 * Conservative: only rejects known-bad patterns. If in doubt, show the image.
 */
export function isBadCoverUrlWithReason(url: string | null | undefined): { bad: boolean; reason?: string } {
  if (url === null || url === undefined) return { bad: true, reason: 'null/undefined' };
  const s = String(url).trim();
  if (s === '') return { bad: true, reason: 'empty string' };
  if (s.includes('coverartarchive.org/release-group/')) return { bad: true, reason: 'coverartarchive.org/release-group/ redirect URL' };
  if (s.startsWith('/') && !s.startsWith('//')) return { bad: true, reason: 'filesystem path, not a URL' };
  if (/^[A-Z]:\\/i.test(s)) return { bad: true, reason: 'Windows filesystem path' };
  if (s.startsWith('\\\\')) return { bad: true, reason: 'UNC path' };
  if (/^[0-9a-f]{64}$/i.test(s)) return { bad: true, reason: 'artwork hash, not a URL' };
  // Generated title-card placeholders (AlbumGrid / LocalAlbumPage) — not real art.
  if (/^data:image\/svg\+xml(?:;base64|,)/i.test(s)) {
    return { bad: true, reason: 'svg placeholder, not album art' };
  }
  return { bad: false };
}

export function isBadCoverUrl(url: string | null | undefined): boolean {
  return isBadCoverUrlWithReason(url).bad;
}

/** True when URL targets the Connector cover-art proxy (hash-addressed). */
export function isCoverProxyUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const s = String(url);
  // 64-char full art and 16-char harvested thumb keys both use /cover/{hash}.
  return /\/cover\/[0-9a-f]{16}(?:[/?&]|$)/i.test(s)
    || /\/cover\/[0-9a-f]{64}(?:[/?&]|$)/i.test(s)
    || /\/artwork\/[0-9a-f]{16}(?:[/?&]|$)/i.test(s)
    || /\/artwork\/[0-9a-f]{64}(?:[/?&]|$)/i.test(s);
}

/** Synchronous track/album cover URL — proxy hash, then online fallbacks. */
export function resolveTrackArtUrlSync(
  track: Record<string, unknown> | null | undefined,
  options?: { thumb?: boolean },
): string {
  if (!track) return '';
  const thumb = options?.thumb !== false;

  const thumbKey = pickCoverArtThumbHash(track);
  if (thumbKey) {
    const diskThumb = buildLocalThumbUrl(thumbKey);
    if (diskThumb) return diskThumb;
    const thumbUrl = buildLocalCoverUrl(thumbKey, { thumb });
    if (thumbUrl) return thumbUrl;
  }

  const embHash = (track.embedded_cover_hash || track.embeddedCoverHash) as string | null | undefined;
  const folHash = (track.folder_cover_hash || track.folderCoverHash) as string | null | undefined;
  const localUrl = buildLocalCoverUrl(embHash, { thumb })
    || buildLocalCoverUrl(folHash, { thumb });
  if (localUrl) return localUrl;

  const candidates = [
    track.artworkUrl,
    track.artUrl,
    track.cover_art_url,
    track.coverUrl,
    track.cover,
    track.imageUrl,
    track.artwork,
  ];

  for (const raw of candidates) {
    if (!raw || isBadCoverUrl(String(raw))) continue;
    let normalized = String(raw).trim();
    if (normalized.startsWith('http://')) {
      normalized = normalized.replace(/^http:\/\//i, 'https://');
    }
    return withCoverThumbParam(normalized) || normalized;
  }
  return '';
}

/** Pre-generated disk thumb must be at least 512 bytes and a known raster type. */
export const MIN_VALID_THUMB_BYTES = 512;

const VALID_THUMB_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);

export function validateThumbBlob(blob: Blob | null | undefined): boolean {
  if (!blob) return false;
  if (blob.size < MIN_VALID_THUMB_BYTES) return false;
  const type = String(blob.type || '').trim().toLowerCase();
  if (!type) return false;
  return VALID_THUMB_IMAGE_TYPES.has(type);
}

export function validateThumbArrayBuffer(
  buffer: ArrayBuffer | null | undefined,
  contentType?: string | null,
): boolean {
  if (!buffer || buffer.byteLength < MIN_VALID_THUMB_BYTES) return false;
  const type = String(contentType || '').trim().toLowerCase();
  if (!type) return false;
  return VALID_THUMB_IMAGE_TYPES.has(type);
}

/** Append ?size=thumb for grid/mini-player tiles (proxy resizes to 200px JPEG). */
export function withCoverThumbParam(url: string | null | undefined): string | null {
  if (!url) return null;
  const s = String(url).trim();
  if (!s || s.startsWith('data:')) return s;
  if (!isCoverProxyUrl(s)) return s;
  if (/[?&]size=thumb(?:&|$)/i.test(s)) return s;
  const sep = s.includes('?') ? '&' : '?';
  return `${s}${sep}size=thumb`;
}

/** True when URL requests the pre-generated disk thumb (Connector /cover/{hash}?size=thumb). */
export function isCoverThumbRequest(url: string | null | undefined): boolean {
  if (!url) return false;
  return /[?&]size=thumb(?:&|$)/i.test(String(url));
}

/** 64-char full art or 16-char harvested thumb key from local-core artwork store. */
export function normalizeArtworkCacheKey(hash: string | null | undefined): string | null {
  if (!hash || typeof hash !== 'string') return null;
  const h = hash.trim().toLowerCase();
  if (/^[0-9a-f]{64}$/.test(h) || /^[0-9a-f]{16}$/.test(h)) return h;
  return null;
}

export function pickCoverArtThumbHash(album: Record<string, unknown> | null | undefined): string | null {
  return normalizeArtworkCacheKey(album?.cover_art_thumb_hash as string | null | undefined);
}

/** Sync local cover URL — thumb/hash proxy keys only (/cover/{hash} is hash-keyed, not album id). */
export function resolveLocalAlbumCoverSync(
  album: Record<string, unknown> | null | undefined,
  options?: { thumb?: boolean },
): string | null {
  const preferThumb = options?.thumb !== false;

  const thumbKey = pickCoverArtThumbHash(album);
  if (thumbKey) {
    const diskThumb = buildLocalThumbUrl(thumbKey);
    if (diskThumb) return diskThumb;
    const thumbUrl = buildLocalCoverUrl(thumbKey, { thumb: preferThumb });
    if (thumbUrl) return thumbUrl;
  }
  const embHash = (album?.embedded_cover_hash || album?.embeddedCoverHash) as string | null | undefined;
  const folHash = (album?.folder_cover_hash || album?.folderCoverHash) as string | null | undefined;
  return buildLocalCoverUrl(embHash, { thumb: preferThumb })
    || buildLocalCoverUrl(folHash, { thumb: preferThumb });
}

/** Strip ?size=thumb so a failed thumb request can fall through to full cover bytes. */
export function withoutCoverThumbParam(url: string | null | undefined): string | null {
  if (!url) return null;
  const s = String(url).trim();
  if (!s || !isCoverThumbRequest(s)) return s;
  try {
    const parsed = new URL(s);
    parsed.searchParams.delete('size');
    const q = parsed.searchParams.toString();
    return `${parsed.origin}${parsed.pathname}${q ? `?${q}` : ''}${parsed.hash}`;
  } catch {
    let out = s.replace(/([?&])size=thumb(?=&|$)/gi, (_, sep) => (sep === '?' ? '?' : ''));
    out = out.replace(/\?&/g, '?').replace(/\?$/, '');
    return out;
  }
}

/** Extract hash segment from Connector cover proxy URL (for 404 logging). */
export function coverProxyHashFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = String(url).match(/\/cover\/([0-9a-f]{64})/i);
  return m ? m[1].slice(0, 16) : null;
}

export function logCoverThumb404(url: string | null | undefined): void {
  console.warn('[cover-art] thumb 404, falling back to full cover', {
    hash: coverProxyHashFromUrl(url),
    url,
  });
}

/** Prefer hi-res track/album art for large hero panels; fall back to resolved URL. */
export function upgradeTrackCoverToHiRes(
  url: string | null | undefined,
  track: Record<string, unknown> | null | undefined,
): string {
  const hiRes = track?.artworkUrlHiRes;
  if (hiRes && !isBadCoverUrl(String(hiRes))) {
    return String(hiRes).trim();
  }
  if (!url) return '';
  let s = String(url).trim();
  if (s.includes('static.qobuz.com')) {
    s = s.replace(/_\d+\.jpg/i, '_org.jpg');
  }
  return withoutCoverThumbParam(s) || s;
}

/** Large playlist hero: now-playing track art when active, else static context cover. */
export function pickPlaylistHeroCoverUrl(
  contextCover: string | null | undefined,
  nowPlayingCover: string | null | undefined,
  followNowPlaying: boolean,
): string | null {
  if (followNowPlaying && nowPlayingCover) return nowPlayingCover;
  return contextCover || null;
}
